/**
 * Import remap dialog (S14) — shown after importing a definition that
 * references connections this machine doesn't have. One picker per missing
 * connection, filtered to ENGINE-COMPATIBLE local connections via the
 * additive `engine` field recorded at authoring time (C1 decision 14; refs
 * without an engine offer every connection). Leaving the ids untouched is a
 * legal outcome — tiles then show their connection-lost state until the user
 * remaps from Configure.
 *
 * ONE primary action (S24 review): the footer button's label follows the
 * selection ("Keep as is" ↔ "Apply remap") instead of offering a "Keep
 * original id" option, a "Keep as is" button AND a disabled "Apply remap"
 * for the same decision.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Connection } from '@/types/electron';
import type { ConnectionRef } from '../../services/definitionIo';
import { safeConnectionName } from '@/types/safeConnectionDisplay';

type ImportRemapDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboardName: string;
  refs: ConnectionRef[];
  connections: Connection[];
  /** Mapping only contains refs the user actually remapped. */
  onApply: (mapping: Record<string, string>) => void;
};

const connectionLabel = (c: Connection): string => safeConnectionName(c);

export function ImportRemapDialog({
  open,
  onOpenChange,
  dashboardName,
  refs,
  connections,
  onApply,
}: ImportRemapDialogProps) {
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const remappedCount = Object.keys(mapping).length;

  const candidatesFor = (ref: ConnectionRef): Connection[] =>
    ref.engine ? connections.filter((c) => (c.engine ?? 'mssql') === ref.engine) : connections;

  const commit = () => {
    // No picks = "keep as is": nothing to rewrite, so don't spend a save.
    if (remappedCount > 0) {
      onApply(mapping);
      window.electronAPI?.analytics
        ?.track('feature:dashboard_import_remap', { count: remappedCount })
        .catch(() => {});
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="import-remap-dialog">
        <DialogHeader>
          <DialogTitle>Remap connections</DialogTitle>
          <DialogDescription>
            “{dashboardName}” references {refs.length === 1 ? 'a connection' : 'connections'} not
            saved on this machine. Pick local replacements, or keep the original ids.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {refs.map((ref) => {
            const candidates = candidatesFor(ref);
            return (
              <div key={ref.connectionId} className="flex flex-col gap-1.5">
                {/* The ghost id alone says nothing about what could replace
                    it — the definition records the authoring engine, so show
                    it as a badge (S24 review). */}
                <Label className="flex items-center gap-1.5 text-xs">
                  <span className="min-w-0 truncate font-mono">{ref.connectionId}</span>
                  <Badge
                    variant="outline"
                    className="shrink-0 text-[10px] font-normal"
                    data-testid={`remap-engine-${ref.connectionId}`}
                  >
                    {ref.engine ?? 'unknown engine'}
                  </Badge>
                </Label>
                <div className="text-xs text-muted-foreground">Used by {ref.usedBy.join(', ')}</div>
                {candidates.length === 0 ? (
                  <div
                    className="text-xs text-warning"
                    data-testid="remap-no-candidates"
                  >
                    No {ref.engine ?? ''} connections saved on this machine — add one, then remap
                    from the tile&apos;s Configure dialog.
                  </div>
                ) : (
                  <Select
                    value={mapping[ref.connectionId] ?? ''}
                    onValueChange={(value) =>
                      setMapping((prev) => ({ ...prev, [ref.connectionId]: value }))
                    }
                  >
                    <SelectTrigger className="h-8" data-testid={`remap-select-${ref.connectionId}`}>
                      <SelectValue placeholder="Pick a local connection…" />
                    </SelectTrigger>
                    <SelectContent>
                      {candidates.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {connectionLabel(c)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          {/* One button, label follows the picks: nothing chosen = "Keep as
              is" (a legal outcome, never a dead end), one or more chosen =
              "Apply remap". */}
          <Button
            onClick={commit}
            title={
              remappedCount === 0
                ? 'Leaves the original connection ids — remap later from each tile’s Configure dialog'
                : `Rewrites ${remappedCount === 1 ? '1 reference' : `${remappedCount} references`} to the connections you picked`
            }
            data-testid="remap-apply"
          >
            {remappedCount === 0 ? 'Keep as is' : 'Apply remap'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
